// React imports
import React, { useEffect, useState } from 'react';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import { XMLParser } from 'fast-xml-parser';

// Material-UI imports
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';

// FontAwesome Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faCircleExclamationCheck, faTimes, faEye } from '@fortawesome/pro-light-svg-icons';

const ORGANIZATIONS = [{
    id: '2643387f-b9c2-4575-a51f-c5d178810e82',
    alias: 'navia',
    name: 'Navia',
}];

const BASE_PATH = '/Organisation';

const PAGE_SIZE = 50;

function FTPOrgUpdates(props) {
    const { accesstoken, onBadge } = props;
    const [ updates, setUpdates ] = useState(ORGANIZATIONS.map(() => []));
    const [ list, setList ] = useState(null);
    const [ working, setWorking ] = useState([]);
    const [ deleting, setDeleting ] = useState([]);
    const [ last, setLast ] = useState(ORGANIZATIONS.map(() => PAGE_SIZE));
    const [ loading, setLoading ] = useState(false);
    const [ sorting, setSorting ] = useState('oldestFirst');

    return (
        <Box sx={{ p: 2 }} >
            Implemented but was a total waste of time.
        </Box>
    )

    useEffect(() => {
        onBadge(<WhiteCircularProgress size={9} />);
        (async () => {
            setLoading(true);
            const _updates = await Promise.all(ORGANIZATIONS.map(async (org, orgIndex) => {
                const { data: organization } = await axios(`https://app.monada.ai/api/organizations/${org.alias}?accesstoken=${accesstoken}`);

                const { ftp } = organization.integrations.cargowise;

                let sorted = null;
                if (!list) {
                    const { data: _list } = await axios.post(`https://app.monada.ai/api/passthroughs/ftp?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                        method: 'list',
                        filename: BASE_PATH,
                        server: ftp.server,
                        username: ftp.username,
                        password: ftp.password,
                    });

                    sorted = _list.sort((a, b) => sorting === 'oldestFirst' ? a.modifyTime - b.modifyTime : b.modifyTime - a.modifyTime);

                    console.log('[FTPOrgUpdates] List', sorted);
                    setList(sorted);
                } else {
                    sorted = list.sort((a, b) => sorting === 'oldestFirst' ? a.modifyTime - b.modifyTime : b.modifyTime - a.modifyTime);
                }

                const contents = await Promise.all(sorted.slice(0, last[orgIndex]).map(async (file) => {
                    const update = updates[orgIndex].find(u => u.name === file.name);
                    if (update && update.content) {
                        return updates[orgIndex].find(u => u.name === file.name);
                    }
                    const { data: content } = await axios.post(`https://app.monada.ai/api/passthroughs/ftp?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                        method: 'get',
                        filename: BASE_PATH + '/' + file.name,
                        server: ftp.server,
                        username: ftp.username,
                        password: ftp.password,
                    });

                    let xml = null;
                    try {
                        xml = new XMLParser({ ignoreAttributes: false }).parse(content);
                    } catch (e) {}

                    return { content, xml };
                }));

                return sorted.map((file, i) => ({
                    ...file,
                    ...contents[i]
                }));
            }));

            const badge = _.sumBy(updates, u => u.length);
            onBadge(badge > 0 ? badge : null);

            setUpdates(_updates);
            setLoading(false);
        })();
    }, [last, sorting]);

    async function onExecute({ org, update }) {
        setWorking(w => [...w, update.name]);

        try {
            const { data: _accounts } = await axios(`https://app.monada.ai/api/accounts?organizationId=${org.id}&accesstoken=${accesstoken}`);
            let cacheAccounts = [];
            if (_accounts.cache) {
                cacheAccounts = (await axios(_accounts.cache)).data;
            }
            const accounts = _.uniqBy([..._accounts.accounts, ...cacheAccounts], 'id');
            
            const { data: _suppliers } = await axios(`https://app.monada.ai/api/suppliers?organizationId=${org.id}&accesstoken=${accesstoken}`);
            const suppliers = _suppliers.suppliers;

            const body = update.xml.UniversalInterchange.Body.Native.Body;
            const isAccount = true; // TODO body.Organization.OrgHeader.OrgCompanyDataCollection.OrgCompanyData.IsDebtor === 'true';
            const isSupplier = body.Organization.OrgHeader.OrgCompanyDataCollection.OrgCompanyData.IsCreditor === 'true';

            if (!isAccount && !isSupplier) {
                throw 'Not an account or supplier';
            }

            const uniqueId = body.Organization.OrgHeader.Code;
            const name = body.Organization.OrgHeader.FullName;
            const account = isAccount ? accounts.find(a => a.uniqueId?.endsWith(`:${uniqueId}`)) || accounts.find(a => (a.name || '').toUpperCase() === name) : null;
            const supplier = isSupplier ? suppliers.find(a => a.uniqueId?.endsWith(`:${uniqueId}`)) || suppliers.find(a => (a.name || '').toUpperCase() === name) : null;

            const addressObj = await getGoogleAddressObj({ organization: body.Organization, isAccount, account, isSupplier, supplier });
            const telephoneNumber = body.Organization.OrgHeader.OrgAddressCollection.OrgAddress.Phone;

            const newAccount = isAccount ? createObj({ base: account, uniqueId, name, organizationId: org.id, phone: telephoneNumber, address: addressObj, inactive: update.xml.UniversalInterchange.Header.RecipientID === 'Monada_cancelled', xml: update.content }) : null;

            const newSupplier = isSupplier ? createObj({ base: supplier, uniqueId, name, organizationId: org.id, phone: telephoneNumber, address: addressObj, inactive: update.xml.UniversalInterchange.Header.RecipientID === 'Monada_cancelled', xml: update.content }) : null;

            if (isAccount) {
                if (account) {
                    await axios.put(`https://app.monada.ai/api/accounts/${account.id}?organizationId=${org.id}&accesstoken=${accesstoken}`, { account: _.omit(newAccount, ['created', 'owner']) });
                } else {
                    await axios.post(`https://app.monada.ai/api/accounts?organizationId=${org.id}&accesstoken=${accesstoken}`, { account: newAccount });
                }
            }

            if (isSupplier) {
                if (supplier) {
                    await axios.put(`https://app.monada.ai/api/suppliers/${supplier.id}?organizationId=${org.id}&accesstoken=${accesstoken}`, { supplier: _.omit(newSupplier, ['created', 'owner'])});
                } else {
                    await axios.post(`https://app.monada.ai/api/suppliers?organizationId=${org.id}&accesstoken=${accesstoken}`, { supplier: newSupplier });
                }
            }

            setWorking(w => w.filter(w => w !== update.name));
        } catch (e) {
            console.error(e);
            setWorking(d => [...d.filter(w => w !== update.name), `${update.name}-error`]);
            await new Promise(res => setTimeout(res, 3000));
            setWorking(d => d.filter(w => w !== `${update.name}-error`));
        }
    }

    async function onDelete({ org, update }) {
        setDeleting([...deleting, update.name]);

        try {
            const { data: organization } = await axios(`https://app.monada.ai/api/organizations/${org.id}?accesstoken=${accesstoken}`);
            const { ftp } = organization.integrations.cargowise;
            const { data: content } = await axios.post(`https://app.monada.ai/api/passthroughs/ftp?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                method: 'delete',
                filename: BASE_PATH + '/' + update.name,
                server: ftp.server,
                username: ftp.username,
                password: ftp.password,
            });
            setDeleting(d => d.filter(w => w.startsWith(update.name)));
            setUpdates(_updates => _updates.map(u => u.filter(f => f.name !== update.name)));
        } catch(e) {
            console.error(e);
            setDeleting(d => [...d.filter(w => w !== update.name), `${update.name}-error`]);
            await new Promise(res => setTimeout(res, 3000));
            setDeleting(d => d.filter(w => w !== `${update.name}-error`));
        }
    }

    async function onOpen({ update }) {
        const win = window.open('', 'Title', 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes');
        win.document.body.innerText = update.content;
    }

    return (
        <Box sx={{ p: 2 }} >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='caption' component={Box}>
                    Sorting:&nbsp;
                </Typography>
                
                <Button
                    variant='text'
                    color='primary'
                    disabled={loading}
                    onClick={() => {
                        setUpdates(ORGANIZATIONS.map(() => []));
                        setSorting(s => s === 'oldestFirst' ? 'newestFirst' : 'oldestFirst')
                    }}
                    sx={{ textTransform: 'none', marginRight: '12px', padding: '0px', minWidth: '0px' }}
                >
                    {sorting === 'oldestFirst' ? 'Old -> new' : 'New -> old'}
                </Button>
                {sorting === 'newestFirst' && (
                    <Alert severity='warning' sx={{ fontSize: '12px' }}>
                        Do not update organizations when viewing newest updates first (you might override a newer update with an older one).
                    </Alert>
                )}
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Organization</TableCell>
                            <TableCell>File</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell></TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ORGANIZATIONS.map((org, i) => {
                            return [
                                ...updates[i].filter(u => !!u.content).map((update, ui) => {
                                    const error = !update.xml || !update?.xml?.UniversalInterchange?.Header?.RecipientID;
                                    return _.compact([(
                                        ui === 0 ? 
                                            <TableRow key={org.name}>
                                                <TableCell colSpan={5} style={{ textAlign: 'center' }}>{org.name}</TableCell>
                                            </TableRow> : null
                                    ), (
                                        <TableRow key={update.name}>
                                            <TableCell>{org.name}</TableCell>
                                            <TableCell>{update.name}</TableCell>
                                            <TableCell>{dayjs(update.modifyTime).fromNow()}</TableCell>
                                            <TableCell>
                                                {error && <Alert severity='error'>Invalid XML content</Alert>}
                                                {!error && (
                                                    <Box>
                                                        {update.xml.UniversalInterchange.Header.RecipientID} - {update.xml.UniversalInterchange.Body.Native.Body.Organization.OrgHeader.Code}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton color='primary' sx={{ marginRight: '12px' }} onClick={() => onOpen({ update })}>
                                                    <FontAwesomeIcon icon={faEye} />
                                                </IconButton>

                                                <IconButton disabled={error || [...working, ...deleting].includes(update.name)} color='primary' sx={{ marginRight: '12px' }} onClick={() => onExecute({ org, update })}>
                                                    {working.includes(update.name) && <CircularProgress size={12} />}
                                                    {working.includes(`${update.name}-error`) && <FontAwesomeIcon icon={faTimes} />}
                                                    {!working.includes(update.name) && !working.includes(`${update.name}-error`) && <FontAwesomeIcon icon={faCircleExclamationCheck} />}
                                                </IconButton>
                                                <IconButton disabled={[...working, ...deleting].includes(update.name)} color='error' onClick={() => onDelete({ org, update })}>
                                                    {deleting.includes(update.name) && <CircularProgress size={12} />}
                                                    {deleting.includes(`${update.name}-error`) && <FontAwesomeIcon icon={faTimes} />}
                                                    {!deleting.includes(update.name) && !deleting.includes(`${update.name}-error`) && <FontAwesomeIcon icon={faTrash} />}
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )]);
                                }).flat(),
                                !loading ? 
                                    <TableRow key={org.name + '-more'}>
                                        <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                                            <Button color='primary' onClick={() => setLast(l => l.map((l, li) => li === i ? l + PAGE_SIZE : l))}>
                                                <FontAwesomeIcon icon={faEye} />
                                                &nbsp; View More ({last[i]}/{list?.length})
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                : null
                            ];
                        }).flat()}
                    </TableBody>
                </Table>
            </TableContainer>
            {loading && (
                <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                </Box>
            )}
        </Box>
    )
}

export default FTPOrgUpdates;

const WhiteCircularProgress = styled(CircularProgress)({
  color: 'white',
});

async function getGoogleAddressObj({ organization, isAccount, account, isSupplier, supplier }) {
    const address = organization.OrgHeader.OrgAddressCollection.OrgAddress;
    const addressText = `${address.Address1} ${address.Address2} ${address.City} ${address.State} ${address.CountryCode.Code} ${address.PostCode}`;

    if ((!isAccount || account?.address?.text === addressText) && (!isSupplier || supplier?.address?.text === addressText)) return account.address;
    const { data: places } = addressText ? await axios.post('https://places.googleapis.com/v1/places:searchText', { textQuery : addressText }, {
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': 'AIzaSyBakZJ-07CCOWPG6Dn7u9nr1WI08aTrPic', 'X-Goog-FieldMask': '*' }
    }) : { data: null };

    if (!places?.places) {
        return null;
    } else {
        return  {
            type: 'geocode',
            text: addressText,
            id: places.places ? places.places[0].id : '',
            countryCode: places.places[0].addressComponents.find(c => c.types.includes('country')).shortText,
        };
    }
}

function createObj({ base, uniqueId, name, organizationId, phone, address, inactive, xml }) {
    return {
        ...base || {},
        uniqueId: base?.uniqueId || `::${uniqueId}`,
        name,
        organizationId,
        phone: '+' + phone,
        address,
        inactive,
        updates: [
            ...base?.updates || [],
            {
                created: new Date().getTime(),
                createdBy: 'cargowise@monada.ai',
                html: `<p>Account/Supplier ${inactive ? 'paused' : base ? 'updated' : 'created'} in Cargowise. <span style="text-decoration: underline; cursor: pointer" onClick="(function () { const html='<html><body><h2>XML</h2><pre>' + atob('${btoa(xml.replace(/</g, '&lt;').replace(/>/g, '&gt;'))}') + '</pre></body></html>'; const blob = new Blob([html], { type: 'text/html'}); const url = window.URL.createObjectURL(blob); window.open(url, '_blank'); })()">Details</span></p>`
            }
        ]
    };
}
